import { combineReducers, Reducer } from 'redux';
import { Project } from '../../../models/Project';
import {
  CREATE_PROJECT_REQUEST,
  CREATE_PROJECT_COMMIT,
  CREATE_PROJECT_ROLLBACK,
  CREATE_SUBPROJECT_REQUEST,
  CREATE_SUBPROJECT_COMMIT,
  CREATE_SUBPROJECT_ROLLBACK,
  UPDATE_PROJECT_REQUEST,
  UPDATE_PROJECT_ROLLBACK,
  UPDATE_PROJECT_COMMIT,
  ADD_TO_PROJECT_FAVORITES_REQUEST,
  ADD_TO_PROJECT_FAVORITES_ROLLBACK,
  REMOVE_FROM_PROJECT_FAVORITES_REQUEST,
  REMOVE_FROM_PROJECT_FAVORITES_ROLLBACK,
  UPDATE_PROJECT_STATUS,
  UPDATE_PROJECT_MASTERPLAN_REQUEST,
  UPDATE_PROJECT_MASTERPLAN_ROLLBACK,
  SET_PROJECT_MASTERPLAN,
  FETCH_PROJECT_BY_ID_COMMIT,
  WS_UPDATE_PROJECT_EXTENSION_ME,
  LEAVE_PROJECT_ME,
  SYNC_PROJECTS_REQUEST,
  SYNC_PROJECTS_COMMIT,
  SYNC_PROJECTS_ROLLBACK,
} from '../actions';
import { ProjectId } from '../../../models/Types';
import { CLEAR_PRIO_CACHE } from '../../../actions';
import { ProjectExtensionAccessStateUpdatedEvent } from '../ws';
import { ProjectExtensionMe } from '../../../models/ProjectExtension';
import { GenericSearchResultItem } from '../../../components/Filter/types';
import { distinct } from '../../../util';

export interface ProjectsState {
  byId: ProjectByIdState;
  allIds: string[];
  myIds: string[];
  redirects: RedirectState;
  meta: ProjectsMeta;
  leftProjects: ProjectId[];
}

export interface ProjectByIdState {
  [projectId: string]: Project;
}

const byId: Reducer<ProjectByIdState, any> = (state = {}, action) => {
  switch (action.type) {
    case SYNC_PROJECTS_COMMIT: {
      const { meProjects, overviewProjects } = action.payload as {
        meProjects: Project[];
        overviewProjects: GenericSearchResultItem<Project>[];
      };
      const newState = { ...state };

      for (const rawItem of meProjects) {
        const item = {
          ...rawItem,
          internalOfficeId: rawItem.internalOfficeId?.toLowerCase(),
        };
        const current = newState[item.projectId];
        newState[item.projectId] = current
          ? {
              ...current,
              ...item,
              projectExtensionMeDtos:
                item.projectExtensionMeDtos ??
                current.projectExtensionMeDtos ??
                null,
            }
          : item;
      }

      for (const { data: rawItem } of overviewProjects) {
        const item = {
          ...rawItem,
          internalOfficeId: rawItem.internalOfficeId?.toLowerCase(),
        };
        newState[item.projectId] = item;
      }

      return newState;
    }
    case FETCH_PROJECT_BY_ID_COMMIT: {
      const { payload } = action;
      return {
        ...state,
        [payload.projectId]: payload,
      };
    }

    /** CREATE */

    case CREATE_PROJECT_REQUEST: {
      const {
        payload: { project },
        meta: { temporaryId },
      } = action;
      return {
        ...state,
        [temporaryId]: {
          projectId: temporaryId,
          ...project,
          isTemporary: true,
        },
      };
    }

    case CREATE_PROJECT_ROLLBACK: {
      const {
        meta: { temporaryId },
      } = action;
      // eslint-disable-next-line
      const { [temporaryId]: project_, ...newState } = state;
      return newState;
    }

    case CREATE_PROJECT_COMMIT: {
      const {
        payload: { projectId },
        meta: { temporaryId },
      } = action;
      const currentProject = state[temporaryId];
      if (!currentProject) return state;
      const { [temporaryId]: project, ...newState } = state;
      return {
        ...newState,
        [projectId]: {
          ...project,
          isTemporary: false,
          projectId,
        },
      };
    }

    case CREATE_SUBPROJECT_REQUEST: {
      const {
        payload: { subproject },
        meta: { temporaryId, parentProject },
      } = action;
      return {
        ...state,
        [temporaryId]: {
          projectId: temporaryId,
          isTemporary: true,
          parentProject: parentProject.projectId,
          name: subproject.name,
          number: subproject.subprojectNumber,
          shortName: subproject.shortName,
        },
      };
    }

    case CREATE_SUBPROJECT_COMMIT: {
      const {
        payload,
        meta: { temporaryId },
      } = action;
      const currentProject = state[temporaryId];
      if (!currentProject) return state;
      const { [temporaryId]: _, ...newState } = state;
      return {
        ...newState,
        [payload.projectId]: {
          ...payload,
          isTemporary: false,
        },
      };
    }

    case CREATE_SUBPROJECT_ROLLBACK: {
      const {
        meta: { temporaryId },
      } = action;
      // eslint-disable-next-line
      const { [temporaryId]: _, ...newState } = state;
      return newState;
    }

    /** UPDATE */
    case UPDATE_PROJECT_REQUEST: {
      const {
        payload: { project },
      } = action;
      if (!state[project.projectId]) return state;
      return {
        ...state,
        [project.projectId]: {
          ...state[project.projectId],
          ...project,
          isUpdating: true,
        },
      };
    }
    case UPDATE_PROJECT_STATUS: {
      const {
        meta: { projectId },
        payload: { projectStatus },
      } = action;
      if (!state[projectId]) return state;
      return {
        ...state,
        [projectId]: {
          ...state[projectId],
          projectStatus,
        },
      };
    }

    case UPDATE_PROJECT_ROLLBACK: {
      const {
        meta: { projectId, rollbackState },
      } = action;
      return {
        ...state,
        [projectId]: {
          ...rollbackState,
        },
      };
    }

    case UPDATE_PROJECT_COMMIT: {
      const {
        meta: { projectId },
        payload: updatedProject,
      } = action;
      if (!state[projectId]) return state;
      return {
        ...state,
        [projectId]: {
          ...updatedProject,
          isUpdating: false,
        },
      };
    }

    case REMOVE_FROM_PROJECT_FAVORITES_ROLLBACK:
    case ADD_TO_PROJECT_FAVORITES_REQUEST: {
      const {
        meta: { projectId },
      } = action;
      if (!state[projectId]) return state;
      return {
        ...state,
        [projectId]: {
          ...state[projectId],
          favorite: true,
        },
      };
    }

    case ADD_TO_PROJECT_FAVORITES_ROLLBACK:
    case REMOVE_FROM_PROJECT_FAVORITES_REQUEST: {
      const {
        meta: { projectId },
      } = action;
      if (!state[projectId]) return state;
      return {
        ...state,
        [projectId]: {
          ...state[projectId],
          favorite: false,
        },
      };
    }

    case SET_PROJECT_MASTERPLAN:
    case UPDATE_PROJECT_MASTERPLAN_REQUEST: {
      const {
        meta: { projectId },
        payload: { masterPlanId },
      } = action;
      return {
        ...state,
        [projectId]: {
          ...state[projectId],
          masterPlanId: masterPlanId,
        },
      };
    }

    case UPDATE_PROJECT_MASTERPLAN_ROLLBACK: {
      const {
        meta: { projectId, rollbackMasterPlanId },
      } = action;
      return {
        ...state,
        [projectId]: {
          ...state[projectId],
          masterPlanId: rollbackMasterPlanId,
        },
      };
    }

    case WS_UPDATE_PROJECT_EXTENSION_ME: {
      const { payload } = action;
      const {
        projectId,
        projectExtensionId,
        extensionAccessState,
      }: ProjectExtensionAccessStateUpdatedEvent = payload;

      const project = state[projectId];
      if (!!project) {
        return {
          ...state,
          [projectId]: {
            ...project,
            projectExtensionMeDtos: project.projectExtensionMeDtos?.map(
              (extension) => {
                if (extension.projectExtensionId === projectExtensionId) {
                  return {
                    ...extension,
                    extensionUserState: extensionAccessState,
                  } as ProjectExtensionMe;
                }
                return extension;
              }
            ),
          },
        };
      }

      return state;
    }

    case CLEAR_PRIO_CACHE: {
      return {};
    }

    default:
      return state;
  }
};

const allIds: Reducer<Array<string>, any> = (state = [], action) => {
  switch (action.type) {
    case SYNC_PROJECTS_COMMIT: {
      const { meProjects, overviewProjects } = action.payload as {
        meProjects: Project[];
        overviewProjects: GenericSearchResultItem<Project>[];
      };
      return distinct([
        ...meProjects.map((p) => p.projectId),
        ...overviewProjects.map(({ data }) => data.projectId),
      ]);
    }

    case CREATE_SUBPROJECT_REQUEST:
    case CREATE_PROJECT_REQUEST: {
      const {
        meta: { temporaryId },
      } = action;
      if (state.includes(temporaryId)) return state;
      return distinct([...state, temporaryId]);
    }

    case CREATE_SUBPROJECT_ROLLBACK:
    case CREATE_PROJECT_ROLLBACK: {
      const {
        meta: { temporaryId },
      } = action;
      return state.filter((id) => id !== temporaryId);
    }

    case CREATE_SUBPROJECT_COMMIT:
    case CREATE_PROJECT_COMMIT: {
      const {
        payload: { projectId },
        meta: { temporaryId },
      } = action;
      if (!state.includes(temporaryId)) return state;
      return distinct([...state.filter((id) => id !== temporaryId), projectId]);
    }

    case FETCH_PROJECT_BY_ID_COMMIT: {
      const {
        payload: { projectId },
      } = action;
      if (state.includes(projectId)) return state;
      return distinct([...state, projectId]);
    }

    case CLEAR_PRIO_CACHE: {
      return [];
    }

    default:
      return state;
  }
};

const myIds: Reducer<Array<string>, any> = (state = [], action) => {
  switch (action.type) {
    case SYNC_PROJECTS_COMMIT: {
      const { meProjects } = action.payload as { meProjects: Project[] };
      return distinct(meProjects.map((p) => p.projectId));
    }

    case CREATE_SUBPROJECT_REQUEST:
    case CREATE_PROJECT_REQUEST: {
      const {
        meta: { temporaryId },
      } = action;
      if (state.includes(temporaryId)) return state;
      return [...state, temporaryId];
    }

    case CREATE_SUBPROJECT_ROLLBACK:
    case CREATE_PROJECT_ROLLBACK: {
      const {
        meta: { temporaryId },
      } = action;
      return state.filter((id) => id !== temporaryId);
    }

    case CREATE_SUBPROJECT_COMMIT:
    case CREATE_PROJECT_COMMIT: {
      const {
        payload: { projectId },
        meta: { temporaryId },
      } = action;
      if (!state.includes(temporaryId)) return state;
      return distinct([...state.filter((id) => id !== temporaryId), projectId]);
    }

    case FETCH_PROJECT_BY_ID_COMMIT: {
      const {
        meta: { projectId, isProjectMember },
      } = action;
      if (!isProjectMember) return state;
      if (state.includes(projectId)) return state;

      return distinct([...state, projectId]);
    }

    case CLEAR_PRIO_CACHE: {
      return [];
    }

    default:
      return state;
  }
};

const leftProjects: Reducer<Array<string>, any> = (state = [], action) => {
  switch (action.type) {
    case LEAVE_PROJECT_ME: {
      const { projectId } = action;
      if (!state.includes(projectId)) {
        return [...state, projectId];
      }
      return state;
    }

    case CLEAR_PRIO_CACHE: {
      return [];
    }

    default:
      return state;
  }
};

export interface RedirectState {
  [temporaryId: string]: ProjectId;
}
const redirects: Reducer<RedirectState, any> = (state = {}, action) => {
  switch (action.type) {
    case CREATE_SUBPROJECT_COMMIT:
    case CREATE_PROJECT_COMMIT: {
      const {
        payload: { projectId },
        meta: { temporaryId },
      } = action;
      return {
        ...state,
        [temporaryId]: projectId,
      };
    }

    case CLEAR_PRIO_CACHE: {
      return {};
    }

    default:
      return state;
  }
};

interface ProjectsMeta {
  isFetching: boolean;
  hasError: boolean;
  errorMessage?: string;
}

const meta: Reducer<ProjectsMeta, any> = (
  state = { hasError: false, isFetching: false },
  action
) => {
  switch (action.type) {
    case SYNC_PROJECTS_REQUEST: {
      return { ...state, isFetching: true };
    }
    case SYNC_PROJECTS_COMMIT: {
      return { ...state, isFetching: false, hasError: false };
    }
    case SYNC_PROJECTS_ROLLBACK: {
      return {
        ...state,
        isFetching: false,
        hasError: true,
        errorMessage: 'projects:errorMessages.fetchError',
      };
    }
    case CLEAR_PRIO_CACHE: {
      return { hasError: false, isFetching: false };
    }
    default:
      return state;
  }
};

export default combineReducers({
  byId,
  allIds,
  myIds,
  redirects,
  meta,
  leftProjects,
});

export const getAllProjects: (state: any) => Array<Project> = (state) => {
  return (state?.myIds ?? [])
    .map((id) => state.byId[id])
    .filter((p) => !!p)
    .sort((a: Project, b: Project) => {
      const numberCompare = a.number.localeCompare(b.number);
      if (numberCompare !== 0) return numberCompare;
      return a.name.localeCompare(b.name);
    });
};
export const getProject: (state: any, projectId: string) => Project = (
  state,
  projectId
) => state.byId[projectId];
export const getProjectByIdState: (state: any) => ProjectByIdState = (state) =>
  state.byId;
export const getRedirect: (state: any, temporaryId: string) => string = (
  state,
  temporaryId
) => state.redirects[temporaryId];

export const getIsFetching: (state: any) => boolean = (state) =>
  state.meta.isFetching;

export const getHasError: (state: any) => boolean = (state) =>
  state.meta.hasError;

export const getErrorMessage: (state: any) => string = (state) =>
  state.meta.errorMessage;

export const getProjectsOverview: (state: any) => Array<Project> = (state) => {
  return (state?.allIds ?? [])
    .map((id) => state.byId[id])
    .filter((p) => !!p)
    .sort((a: Project, b: Project) => {
      const numberCompare = a.number.localeCompare(b.number);
      if (numberCompare !== 0) return numberCompare;
      return a.name.localeCompare(b.name);
    });
};

export const getMyProjectIds: (state: ProjectsState) => ProjectId[] = (state) =>
  state.myIds;

export const getAllProjectIds: (state: ProjectsState) => ProjectId[] = (
  state
) => state.allIds;

export const getLeftProjects: (state: ProjectsState) => ProjectId[] = (state) =>
  state.leftProjects;
